<template>
  <div
    class="upload-media-image"
    @click="upload"
  >
    <div
      class="upload-media-image__content upload-content"
      :class="{ 'upload-content--loading': loading, 'upload-content--dragover': dragover }"
      @dragenter.prevent
      @dragover.prevent="dragover = droppable"
      @dragleave="dragover = false"
      @drop.prevent="dropped"
    >
      <slot />
    </div>
    <input
      ref="file"
      type="file"
      class="upload-content__file"
      :accept="mimeTypesAccepted"
      :multiple="false"
      @change="inputChanged"
    >
    <input
      v-model="uuid"
      type="hidden"
      class="upload-content_uuid"
      :name="inputName"
    >
  </div>
</template>

<script>
import { values } from 'lodash';
import Logger from '@tools/logger/logger';
import requests from '@tools/requests';
import { flashBagError } from '@tools/flashbag';
import { URL_XHR_UPLOAD_MEDIA_IMAGE } from '@tools/routes/routes';
import { trans, transchoice } from '@elements/translation';

export default {
  props: {
    modelValue: { type: String, default: '' },
    inputName: { type: String, default: null },
    droppable: { type: Boolean, default: false },
  },
  emits: [ 'loading', 'done', 'update:modelValue' ],
  data() {
    return {
      mimeTypesAccepted: values(window.IMAGE_FORMATS_MIMETYPES).join(', '),
      loading: false,
      fileSizeLimit: 20, // max file size on client-side validation, cannot exceed 50mB
      dragover: false,
    };
  },
  computed: {
    uuid: {
      get() {
        return this.modelValue;
      },
      set(uuid) {
        this.$emit('update:modelValue', uuid);
      },
    },
  },
  methods: {
    dropped(event) {
      this.dragover = false;
      if (this.loading || !this.droppable) {
        return;
      }
      event.preventDefault();
      const files = [];
      [ ...event.dataTransfer.items ].forEach((item) => {
        if (item.kind === 'file') {
          files.push(item.getAsFile());
        }
      });
      if (files.length > 1) {
        flashBagError(trans('upload.count_exceeded.single'));
        return;
      }
      this.process(files[0]);
    },
    upload() {
      if (this.loading) {
        return;
      }
      this.$refs.file.click();
    },
    inputChanged() {
      const input = this.$refs.file;
      if (!input) {
        return;
      }
      const file = input.files[0];
      if (!file) {
        return;
      }
      this.process(file);
    },
    async process(file) {
      if (this.loading) {
        return;
      }
      if (!this.checkSize(file)) {
        this.$refs.file.value = '';
        flashBagError(trans('upload.size_exceeded', { max: this.fileSizeLimit }));
        return;
      }
      this.loading = true;
      this.$emit('loading');
      const formData = new FormData();
      formData.append('file', file);
      try {
        const response = await requests.post(URL_XHR_UPLOAD_MEDIA_IMAGE, formData);
        this.uuid = response.data;
      } catch (error) {
        Logger.error(error);
        flashBagError(transchoice('media.images.upload_error', { count: 1 }));
      } finally {
        this.$refs.file.value = '';
        this.loading = false;
        this.$emit('done');
      }
    },
    checkSize(file) {
      const fileSizeMb = (file.size / (1024 * 1024));
      return fileSizeMb < this.fileSizeLimit;
    },
  },
};
</script>
